import React from 'react';

import { FormikValues } from 'formik';

import {
  adminActions,
  assignPharmacyOwnership,
  removePharmacyOwnership,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import OwnershipModal from '../../../components/Admin/PharmacyList/OwnershipModal';
import DeactivateDialog from '../../../components/Admin/PharmacistList/DeactivateDialog';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import { DeactivateModalIcon } from '../../../helpers/Constants';
import { setDialog } from '../../../reducers/dialogReducer';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';
import strings from '../../../localization';

const { assignPharmacyOwnership: assignAction, removePharmacyOwnership: removeAction } = adminActions;

const ownershipActions = [assignAction, removeAction];

const dialogConfig = {
  heading: {
    title: '',
  },
  showCloseButton: false,
};

/**
 * Ownership mutations for pharmacy accounts. Shared by the per-row action menu
 * and the multi-select bulk assignment, so both go through the same dialogs and
 * the same payload shape.
 */
const usePharmacyOwnership = () => {
  const dispatch = useAppDispatch();

  const assignSuccess = useAppSelector((state) =>
    successSelector([assignAction], state));
  const removeSuccess = useAppSelector((state) =>
    successSelector([removeAction], state));

  // One call covers the whole selection -- the row action just sends a single id.
  const assignOwner = (pharmacyIds: Array<string>) =>
    (ownerId: string) => {
      dispatch(assignPharmacyOwnership({ pharmacyIds, ownerId }));
    };

  const showAssignOwner = (
    pharmacyIds: Array<string>,
    currentOwnerId?: null | string,
  ) =>
    () => {
      dispatch(
        setDialog({
          ...dialogConfig,
          Component: (
            <OwnershipModal
              pharmacyIds={pharmacyIds}
              currentOwnerId={currentOwnerId}
              onConfirm={assignOwner(pharmacyIds)}
            />
          ),
        }),
      );
    };

  const removeOwner = (pharmacyId: string, ownerId: string) =>
    (values: FormikValues) => {
      const { reason } = values ?? {};

      dispatch(removePharmacyOwnership({ pharmacyId, ownerId, reason }));
    };

  const showRemoveOwner = (pharmacyId: string, ownerId: string) =>
    () => {
      dispatch(
        setDialog({
          ...dialogConfig,
          showCloseButton: true,
          Component: (
            <DeactivateDialog
              id={pharmacyId}
              iconType={DeactivateModalIcon.warning}
              heading={strings.removeOwnership}
              confirmationMessage={strings.areYouSureRemoveOwnership}
              onSubmit={removeOwner(pharmacyId, ownerId)}
            />
          ),
        }),
      );
    };

  /** Clears the status and surfaces the shared update message. */
  const acknowledgeOwnershipChange = () => {
    showSuccess(dispatch, strings.updatedSuccessfullyFormat);
    dispatch(resetStatus(ownershipActions));
  };

  return {
    showAssignOwner,
    showRemoveOwner,
    ownershipSuccess: assignSuccess || removeSuccess,
    acknowledgeOwnershipChange,
  };
};

export default usePharmacyOwnership;
